import { MissionResult } from '../missions/MissionManager';
import { StVOInfraction } from '../stvo/InfractionLog';

export class ExamResultModal {
  public container: HTMLElement;
  private onWatchReplayCallback: (() => void) | null = null;
  private onRetryCallback: (() => void) | null = null;
  private onMenuCallback: (() => void) | null = null;

  constructor(private result: MissionResult, private infractions: StVOInfraction[]) {
    this.container = document.createElement('div');
    this.container.id = 'exam-result-modal';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(9, 13, 22, 0.92);
      backdrop-filter: blur(14px);
      z-index: 50;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 16px;
      pointer-events: auto;
    `;

    this.buildUI();
  }

  private buildUI() {
    const card = document.createElement('div');
    card.id = 'exam-result-card';
    card.style.cssText = `
      background: #0f172a;
      border: 2px solid ${this.result.passed ? '#22c55e' : '#ef4444'};
      border-radius: 20px;
      width: min(640px, 95vw);
      max-height: 94vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding: clamp(14px, 3.5vw, 26px);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.85);
      display: flex;
      flex-direction: column;
      gap: 16px;
      box-sizing: border-box;
    `;

    // 1. Result Header Banner
    const banner = document.createElement('div');
    banner.style.cssText = `
      background: ${this.result.passed ? 'linear-gradient(135deg, #14532d 0%, #15803d 100%)' : 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)'};
      border-radius: 14px;
      padding: 16px;
      text-align: center;
      color: white;
    `;
    banner.innerHTML = `
      <div style="font-size:32px;margin-bottom:2px;">${this.result.passed ? '🎉' : '❌'}</div>
      <h2 style="font-size:clamp(18px, 3.5vw, 24px);font-weight:800;margin:0;letter-spacing:0.5px;">${this.result.passed ? 'FAHRPRÜFUNG BESTANDEN' : 'NICHT BESTANDEN'}</h2>
      <p style="font-size:12px;opacity:0.9;margin:4px 0 0 0;">${this.result.missionTitle}</p>
    `;
    card.appendChild(banner);

    // 2. Stats Grid
    const statsGrid = document.createElement('div');
    statsGrid.style.cssText = `display: grid; grid-template-columns: repeat(auto-fit, minmax(85px, 1fr)); gap: 8px;`;

    const createStat = (label: string, value: string, color: string) => `
      <div style="background:#1e293b;border:1px solid #334155;border-radius:10px;padding:10px;text-align:center;">
        <div style="font-size:11px;color:#94a3b8;font-weight:700;">${label}</div>
        <div style="font-size:20px;font-weight:800;color:${color};margin-top:2px;">${value}</div>
      </div>
    `;

    statsGrid.innerHTML = `
      ${createStat('ERGEBNIS', `${this.result.score}%`, this.result.passed ? '#4ade80' : '#f87171')}
      ${createStat('DAUER', `${this.result.durationSeconds}s`, '#38bdf8')}
      ${createStat('FEHLER', `${this.result.infractionsCount}`, this.result.fatalCount > 0 ? '#ef4444' : '#f59e0b')}
    `;
    card.appendChild(statsGrid);

    // 3. Examiner Feedback
    const feedbackBox = document.createElement('div');
    feedbackBox.style.cssText = `
      background: #1e293b;
      border-left: 4px solid ${this.result.passed ? '#22c55e' : '#ef4444'};
      padding: 12px 16px;
      border-radius: 8px;
      font-size: 13.5px;
      color: #e2e8f0;
      line-height: 1.4;
    `;
    feedbackBox.innerHTML = `<strong>Prüfer-Gutachten:</strong> ${this.result.feedback}`;
    card.appendChild(feedbackBox);

    // 4. Detailed Infractions List
    if (this.infractions.length > 0) {
      const infSection = document.createElement('div');
      infSection.innerHTML = `<h4 style="font-size:13px;font-weight:700;color:#f87171;text-transform:uppercase;margin:0 0 8px 0;">Protokollierte StVO-Verstöße (${this.infractions.length})</h4>`;

      const infList = document.createElement('div');
      infList.style.cssText = `display:flex;flex-direction:column;gap:8px;max-height:160px;overflow-y:auto;`;

      this.infractions.forEach((inf) => {
        const item = document.createElement('div');
        item.style.cssText = `
          background: rgba(220, 38, 38, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 12px;
        `;
        item.innerHTML = `
          <div style="display:flex;justify-content:space-between;font-weight:700;color:#fca5a5;">
            <span>${inf.code}: ${inf.title}</span>
            <span style="font-size:10px;padding:2px 6px;border-radius:4px;background:#7f1d1d;color:white;">-${inf.pointsDeducted} PKT</span>
          </div>
          <div style="color:#cbd5e1;margin-top:2px;">${inf.description}</div>
        `;
        infList.appendChild(item);
      });

      infSection.appendChild(infList);
      card.appendChild(infSection);
    }

    // 5. Action Buttons (Replay, Retry, Menu)
    const actionRow = document.createElement('div');
    actionRow.style.cssText = `display: flex; flex-direction: column; gap: 10px; margin-top: 4px;`;

    // Watch Replay Button
    const btnReplay = document.createElement('button');
    btnReplay.id = 'exam-btn-replay';
    btnReplay.innerHTML = '🎥 Manöver-Replay analysieren (3 Kameraperspektiven)';
    btnReplay.style.cssText = `
      background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
      color: white;
      border: 1px solid #38bdf8;
      border-radius: 12px;
      padding: 14px;
      min-height: 48px;
      font-size: 14px;
      font-weight: 800;
      cursor: pointer;
      touch-action: manipulation;
      box-shadow: 0 6px 20px rgba(2, 132, 199, 0.4);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    btnReplay.addEventListener('click', () => {
      if (this.onWatchReplayCallback) this.onWatchReplayCallback();
    });

    const subActionRow = document.createElement('div');
    subActionRow.style.cssText = `display: grid; grid-template-columns: 1fr 1fr; gap: 10px;`;

    const btnRetry = document.createElement('button');
    btnRetry.id = 'exam-btn-retry';
    btnRetry.innerText = '🔄 Erneut versuchen';
    btnRetry.style.cssText = `
      background: #16a34a;
      color: white;
      border: none;
      border-radius: 10px;
      padding: 12px;
      min-height: 48px;
      font-weight: 700;
      font-size: 13.5px;
      cursor: pointer;
      touch-action: manipulation;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    btnRetry.addEventListener('click', () => {
      if (this.onRetryCallback) this.onRetryCallback();
    });

    const btnMenu = document.createElement('button');
    btnMenu.id = 'exam-btn-menu';
    btnMenu.innerText = '📋 Andere Aufgabe';
    btnMenu.style.cssText = `
      background: #334155;
      color: white;
      border: none;
      border-radius: 10px;
      padding: 12px;
      min-height: 48px;
      font-weight: 700;
      font-size: 13.5px;
      cursor: pointer;
      touch-action: manipulation;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    btnMenu.addEventListener('click', () => {
      if (this.onMenuCallback) this.onMenuCallback();
    });

    subActionRow.appendChild(btnRetry);
    subActionRow.appendChild(btnMenu);

    actionRow.appendChild(btnReplay);
    actionRow.appendChild(subActionRow);
    card.appendChild(actionRow);

    this.container.appendChild(card);
  }

  public onWatchReplay(cb: () => void) {
    this.onWatchReplayCallback = cb;
  }

  public onRetry(cb: () => void) {
    this.onRetryCallback = cb;
  }

  public onMenu(cb: () => void) {
    this.onMenuCallback = cb;
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
  }

  public unmount() {
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
